import { v4 as uuidv4 } from 'uuid'

export const naturalSortComparer = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })

export const naturalSort = (arr: string[]) => arr.sort(naturalSortComparer)

// Function to determine if a string is numeric or alphabetic
const isNumeric = (str: string) => /^\d+$/.test(str)
const isAlphabetic = (str: string) => /^[A-Za-z]+$/.test(str)
const spaceDigitOrAllLettersPatters = / (\d+|[A-Za-z]+)/

export const customNaturalComparer = (a: string, b: string) => {
  if (typeof a !== 'string' || typeof b !== 'string') {
    return 0
  }
  const [baseA, postfixA] = a.split(spaceDigitOrAllLettersPatters).filter(Boolean)
  const [baseB, postfixB] = b.split(spaceDigitOrAllLettersPatters).filter(Boolean)

  if (!postfixA || !postfixB) {
    return a.localeCompare(b, undefined, { sensitivity: 'base' })
  }

  // Compare base names first (e.g., 'Item', 'Attribute')
  if (baseA !== baseB) {
    return baseA.localeCompare(baseB, undefined, { sensitivity: 'base' })
  }

  // If both postfixes are numeric, sort them as numbers
  if (isNumeric(postfixA) && isNumeric(postfixB)) {
    return Number(postfixA) - Number(postfixB)
  }

  // If both postfixes are alphabetic, sort them taking into account string length
  if (isAlphabetic(postfixA) && isAlphabetic(postfixB)) {
    if (postfixA.length !== postfixB.length) {
      return postfixA.length - postfixB.length // Sort by length first
    }
    return postfixA.localeCompare(postfixB) // Then alphabetically if lengths are equal
  }

  // If one postfix is numeric and the other is alphabetic
  if (isNumeric(postfixA)) return -1 // Numeric comes before alphabetic
  if (isNumeric(postfixB)) return 1 // Alphabetic comes after numeric

  return 0
}
// Custom sort function for handling both numeric and alphabetic indices
export const customizedNaturalSort = (arr: string[]) => arr.sort(customNaturalComparer)

export type LabelledOptionItem = { label?: string; name?: string }

export const optionsSortComparerMaker = (ascending: boolean) => (a: LabelledOptionItem, b: LabelledOptionItem) => {
  const [aValue, bValue] = [a.label ?? a.name ?? '', b.label ?? b.name ?? ''] as const
  return false === ascending ? customNaturalComparer(bValue, aValue) : customNaturalComparer(aValue, bValue)
}

export function splitString(str: string, separator: string, maxParts: number = 1): string[] {
  const parts = str.split(separator)
  if (parts.length <= maxParts) {
    return parts
  }
  return [...parts.slice(0, maxParts), parts.slice(maxParts).join(separator)]
}
export function generateUID<T extends string>() {
  // ########-####-####-####-############
  // slower: `${uid.slice(0, 8)}${uid.slice(9, 13)}${uid.slice(14, 18)}${uid.slice(19, 23)}${uid.slice(24, 36)}`
  // https://jsbench.me/asm2acfyx7/1
  return uuidv4().replace('-', '') as T
}

export const truncateStringToLength = (input: string, length: number, tail = '&hellip;') =>
  input ? (input.length > length ? `${input.slice(0, Math.max(0, length))}${tail}` : input) : ''
